import { INTENT_TYPE } from '../constants';
import { type IntentContext, type IntentResponse } from '../types';
import { createFeatureContext } from './features/feature-context';
import { resolveMatchers } from './resolve-matchers';

/**
 * The convergence rules engine.
 *
 * SOURCE: "Search x Chat Convergence - MVP Triggering Architecture", Normative
 * evaluation order.
 * https://hello.atlassian.net/wiki/spaces/jirasearch/pages/7525895377
 *
 * **First match wins.** Evaluation stops at the winner, and because features are lazy,
 * no lower rule's features are computed either.
 *
 * `pattern` identifies the winning rule one-to-one, so the response needs nothing else to
 * attribute the decision — and `NO_PATTERN` is what the terminal rule reports, so it is
 * also the answer to "did anything claim this input?". Whatever reaches the terminal rule
 * is residual, which is precisely the traffic the remote layer (the design's SLM) exists
 * to judge, so `getInputIntentMultilayer` consults the next layer for it.
 *
 * `recentActivities` are the surface's recently viewed items, consulted only by the
 * `S-RECENT-*` Search protections. Without them those rules decline and nothing else moves.
 */
export const getInputIntentLocal = (
	input: string,
	recentActivities?: IntentContext['recentActivities'],
): IntentResponse => {
	const features = createFeatureContext(input, recentActivities);
	const matched = resolveMatchers().find((rule) => rule.matches(features));

	// The terminal rule matches everything, so `matched` is always set. The fallback keeps
	// that from being an assertion, and produces the same response the terminal rule would.
	const pattern = matched?.pattern ?? 'NO_PATTERN';

	return {
		didMatch: pattern !== 'NO_PATTERN',
		type: matched?.type ?? INTENT_TYPE.FPS,
		pattern,
	};
};
